import { QueryClient, QueryFunction, MutationCache } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    let message = text;
    try {
      const json = JSON.parse(text);
      message = json.message || json.error || text;
    } catch {
      // not JSON, use raw text
    }
    throw new Error(message);
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { "Content-Type": "application/json" } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    // Guard: detect non-primitive queryKey segments before they produce "[object Object]" URLs
    const segments = queryKey as any[];
    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      if (seg !== null && seg !== undefined && typeof seg === "object") {
        const err = new Error(
          `queryKey[${i}] is an object — pass a primitive (id, string, number) instead. Key: ${JSON.stringify(segments)}`
        );
        console.error(err);
        throw err;
      }
    }
    const url = segments.filter(s => s !== undefined && s !== null).join("/");
    const res = await fetch(url, {
      credentials: "include",
      // Prevent browser HTTP cache from serving stale API responses.
      // Without this, re-fetches triggered by invalidateQueries can receive a
      // cached browser response even though the server data has changed.
      cache: "no-store",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  // ── Global mutation success handler ────────────────────────────────────────
  // After ANY mutation succeeds, immediately refetch every query that is
  // currently being observed by a mounted component. This is the reliable
  // solution to the React 18 automatic-batching + TanStack Query v5 issue
  // where invalidateQueries() scheduled refetches can be lost when React
  // batches the setState calls that happen in the same onSuccess callback
  // (e.g. setDialogOpen(false)).
  //
  // Using MutationCache avoids relying on each page's shopId closure being
  // correct at the time the callback fires. The refetch targets only "active"
  // queries (mounted components) so it is efficient.
  mutationCache: new MutationCache({
    onSuccess: () => {
      queryClient.refetchQueries({ type: "active" });
    },
  }),
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
